import { Controller } from "@hotwired/stimulus"
import * as FilePond from "filepond"
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size"
import FilePondPluginImagePreview from "filepond-plugin-image-preview"

import "filepond/dist/filepond.min.css"
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css"
FilePond.registerPlugin(FilePondPluginImagePreview)
FilePond.registerPlugin(FilePondPluginFileValidateSize)

const photoIcon = (classes = "") => {
  return `
    <svg class="${classes}" id="photo-o" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
    </svg>
  `
}

const arrowUpIcon = (classes = "") => {
  return `
    <svg id="arrow-up-tray" class="${classes}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path fill-rule="evenodd" d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clip-rule="evenodd"/>
    </svg>
  `
}

const label = (image) => `
<div class="filepond--label-action flex flex-col gap-2 items-center justify-center text-gray-400">
  ${
    image.length > 0
      ? // The fixed-height wrapper keeps the label the same size before and
        // after the image loads: FilePond measures the label only once, at
        // creation, and freezes the panel at that height — an image that
        // grows later would overflow it.
        `<div class="flex h-24 items-center justify-center"><img src="${image}" class="h-24 rounded-lg shadow object-cover mx-auto" /></div>`
      : photoIcon("w-24 h-24")
  }
  <div class="flex items-center justify-center py-2 gap-2">
    ${arrowUpIcon("w-5 h-5")}
    <div class="font-display no-underline !text-sm upload-label">${image.length > 0 ? "Replace" : "Upload"} File</div>
  </div>
</div>
`

export default class extends Controller {
  static targets = ["input"]

  static values = {
    allowImagePreview: { default: false, type: Boolean },
    // Already-uploaded files to show again after a re-render, as
    // [{name, size, source, type}] where source is the upload's signed id.
    initialFiles: { default: [], type: Array },
    maxFiles: Number,
    maxFileSize: { default: "25MB", type: String },
    replaceImage: { default: "", type: String },
    // Show the server's plain-text error body on a file that failed to upload.
    serverErrors: { default: false, type: Boolean },
    uploadMode: { default: "active_storage", type: String },
  }

  connect() {
    // Legacy callers attach the controller to the input, which FilePond removes.
    // Wrappers keep the controller connected and own the pond's lifecycle.
    this.wrapped = this.hasInputTarget
    this.input = this.wrapped ? this.inputTarget : this.element
    if (this.wrapped) {
      this.beforeCache = () => this.destroyPond()
      document.addEventListener("turbo:before-cache", this.beforeCache)
    }

    // FilePond measures its drop label once at creation and freezes the
    // panel at that height. A URL-sourced preview (unlike an inline data
    // URI) hasn't loaded yet at that point, so the panel comes out too
    // short and the image overflows it — preload the image first, then
    // initialize.
    const replaceImage = this.replaceImageValue
    if (replaceImage && !replaceImage.startsWith("data:")) {
      const image = (this.previewImage = new Image())
      image.onerror = () => this.createPond()
      image.onload = () => this.createPond()
      image.src = replaceImage
    } else {
      this.createPond()
    }
  }

  createPond() {
    // The preload above resolves asynchronously; a Turbo navigation may have
    // removed the field in the meantime.
    if (!this.element.isConnected || !this.input.isConnected || this.pond) return

    const form = this.input.closest("form")
    const options = {
      allowImagePreview: this.allowImagePreviewValue,
      credits: false,
      labelIdle: label(this.replaceImageValue),
      maxFileSize: this.maxFileSizeValue,
    }

    if (this.hasMaxFilesValue) {
      options.maxFiles = this.maxFilesValue
    }

    if (this.initialFilesValue.length > 0) {
      options.files = this.initialFilesValue.map(({ name, size, source, type }) => ({
        options: { file: { name, size, type }, type: "limbo" },
        source,
      }))
    }

    if (this.directUploadMode) {
      options.allowProcess = false
      options.storeAsFile = true
    } else {
      if (this.serverErrorsValue) {
        options.labelFileProcessingError = (error) => error?.body || "Error during upload"
      }

      options.server = {
        url: "/filepond",
        headers: {
          // Optional: the meta tag is absent when forgery protection is off
          // (browser system tests), and throwing here would kill connect().
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        process: {
          ondata: (formData) => {
            this.updateUploadLabel("Upload more files")

            const inputNameParts = this.getInputNameParts()

            for (let pair of formData.entries()) {
              if (typeof pair[1].name == "string") {
                const newData = new FormData()
                newData.append("attribute_name", inputNameParts.attribute)
                newData.append("model_name", inputNameParts.model || "")
                newData.append("file", pair[1])

                const contentTypeInput = document.createElement("input")
                const contentTypeInputName = `${inputNameParts.attribute}_content_type`

                contentTypeInput.name = inputNameParts.model
                  ? `${inputNameParts.model}[${contentTypeInputName}]`
                  : contentTypeInputName

                contentTypeInput.type = "hidden"
                contentTypeInput.value = pair[1].type

                form.appendChild(contentTypeInput)

                return newData
              }
            }

            return formData
          },
          onerror: (body) => body,
        },
      }
    }

    this.pond = FilePond.create(this.input, options)

    if (!this.directUploadMode) {
      this.submitForm = form
      this.preventBusySubmit = (event) => {
        if (this.pond?.status === FilePond.Status.BUSY) event.preventDefault()
      }
      form.addEventListener("submit", this.preventBusySubmit)
    }
  }

  destroyPond() {
    if (this.previewImage) {
      this.previewImage.onerror = null
      this.previewImage.onload = null
      this.previewImage = null
    }
    this.submitForm?.removeEventListener("submit", this.preventBusySubmit)
    this.submitForm = null
    this.preventBusySubmit = null
    this.pond?.destroy()
    this.pond = null
  }

  get directUploadMode() {
    return this.uploadModeValue === "direct"
  }

  disconnect() {
    if (!this.wrapped) return

    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.destroyPond()
  }

  getInputNameParts() {
    const name = this.input.getAttribute("name")
    const regex = /^(\w+)(\[(\w+)\])?/
    const match = name.match(regex)

    let attribute = match[3]
    let model = match[1]

    if (!attribute) {
      attribute = model
      model = null
    }

    return { attribute, model }
  }

  updateUploadLabel(text) {
    const uploadLabel = this.pond?.element.querySelector(".upload-label")

    if (uploadLabel) {
      uploadLabel.innerText = text
    }
  }
}
